type Primitive = string | number | boolean | null | undefined;

/**
 * We define the input as a record where values can be
 * either nested objects or primitive metadata.
 */
interface NestedResponses {
  [key: string]: Record<string, any> | Primitive;
}

export function flattenResponses<T extends Record<string, any>>(
  originalData: NestedResponses,
): T {
  const finalResult = {} as T;

  Object.keys(originalData).forEach((key) => {
    const value = originalData[key];

    // Check if the value is a non-array object to safely merge
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(finalResult, value);
    } else {
      // If it's a primitive, we keep the original top-level key
      (finalResult as any)[key] = value;
    }
  });

  return finalResult;
}

/**
 * Removes non-JS friendly keys and internal metadata from the sandbox.
 */
export function cleanupSandbox(data: Record<string, any>): Record<string, any> {
  const clean: Record<string, any> = {};

  // Define a list of internal keys we explicitly want to remove
  const blacklist = [
    'browser-fingerprint',
    'page-timer-tracker',
    'debug_metadata',
  ];

  Object.keys(data).forEach((key) => {
    // 1. Skip if it's in our explicit blacklist
    if (blacklist.some((term) => key.includes(term))) {
      return;
    }

    // 2. Skip if the key contains characters that break JS variables (like hyphens)
    // Valid JS variables can only contain letters, numbers, $, and _
    if (/[^a-zA-Z0-9$_]/.test(key)) {
      return;
    }

    // 3. Skip if it starts with a number (Invalid JS variable start)
    if (/^[0-9]/.test(key)) {
      return;
    }

    // If it passed the tests, add it to the clean sandbox
    clean[key] = data[key];
  });

  return clean;
}
